import { useEffect, useState } from 'react';
import { User } from '../../interfaces/backend/User';
import { AWSError, DynamoDB } from 'aws-sdk';
import { setAWSCreds } from '../../utils/accessToken';
import { useHistory, useParams } from 'react-router-dom';
import { api } from '../../utils/axiosConfig';
import {
  Alert,
  Button,
  Col,
  Input,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
  Typography
} from 'antd';
import { Content } from 'antd/es/layout/layout';
import { PromiseResult } from 'aws-sdk/lib/request';
import Loading from '../loading';
import {
  AndroidFilled,
  AppleFilled,
  ChromeFilled,
  DeleteOutlined,
  ZoomInOutlined
} from '@ant-design/icons';
import NotificationAPIClient from 'notificationapi-js-client-sdk';
import { readWebsiteVariable } from '../../utils/readWebsiteVariable';
import Paragraph from 'antd/es/typography/Paragraph';
import Link from 'antd/es/typography/Link';
import crypto from 'crypto';

const Text = Typography.Text;

const UsersContainer = (): JSX.Element => {
  const [users, setUsers] = useState<User[]>();
  const accountId = useParams<{ accountId: string }>().accountId;
  const [prefsUserId, setPrefsUserId] = useState<string>();
  const [loading, setLoading] = useState<Record<string, boolean | undefined>>(
    {}
  );
  const history = useHistory();

  // Fetch default set of users
  useEffect(() => {
    getUsers(accountId).then((u) => {
      setUsers(u);
    });
  }, [accountId]);
  const [search, setSearch] = useState<string>('');

  // load user preferences
  useEffect(() => {
    if (!prefsUserId) return;
    const notificationapi = new NotificationAPIClient({
      clientId: accountId,
      userId: prefsUserId,
      userIdHash: crypto
        .createHmac('sha256', accountId)
        .update(prefsUserId)
        .digest('base64'),
      websocket: 'wss://ws.' + readWebsiteVariable('REACT_APP_domain')
    });
    notificationapi.showUserPreferences();
    setPrefsUserId(undefined);
  }, [prefsUserId, accountId]);

  const onUserDataDelete = (userId: string) => {
    setLoading((prev) => {
      return {
        ...prev,
        [userId]: true
      };
    });
    api('DELETE', `${accountId}/users/${userId}`, {}).then(() => {
      if (accountId) {
        getUsers(accountId).then((u) => {
          setUsers(u);
          setLoading((prev) => {
            return {
              ...prev,
              [userId]: undefined
            };
          });
        });
      }
    });
  };

  if (!users) return <Loading />;
  return (
    <Content style={{ padding: 24 }}>
      <Row>
        <Col>
          <Alert
            closable
            onClose={() => {
              localStorage.setItem('hideUsersAlert', 'true');
            }}
            style={{
              display:
                localStorage.getItem('hideUsersAlert') === 'true'
                  ? 'none'
                  : 'flex'
            }}
            type="info"
            description={
              <Paragraph style={{ marginTop: 20 }}>
                You can store end-user information and user preferences in
                NotificationAPI in two ways:
                <br />
                1) Automatically, when you send them a notification
                <br />
                2) Manually, by using our{' '}
                <Link
                  href="https://docs.notificationapi.com/reference/server#identifyuser"
                  target="_blank"
                >
                  identify
                </Link>{' '}
                and{' '}
                <Link
                  href="https://docs.notificationapi.com/quick-start/manage-preferences#programmatically-update-preferences"
                  target="_blank"
                >
                  preferences
                </Link>{' '}
                APIs
              </Paragraph>
            }
          />
        </Col>
      </Row>
      <Row justify="space-between" style={{ marginTop: 20 }}>
        <Col>
          <Space>
            <Text type="secondary">Search User:</Text>
            <Input.Search
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </Space>
        </Col>
        <Col>
          <Text type="secondary">Total Users: {users?.length}</Text>
        </Col>
      </Row>

      <Table
        style={{ marginTop: 20 }}
        pagination={
          users?.length > 100
            ? {
                pageSize: 100,
                showSizeChanger: true,
                pageSizeOptions: ['100', '1000', '10000']
              }
            : false
        }
        dataSource={
          search
            ? users.filter((u) => {
                return (
                  (u.id && u.id.includes(search)) ||
                  (u.email && u.email.includes(search)) ||
                  (u.number && u.number.includes(search))
                );
              })
            : users
        }
        rowKey={(record) => record.id}
        columns={[
          {
            title: 'ID',
            dataIndex: 'userId',
            key: 'userId'
          },
          {
            title: 'Email',
            dataIndex: 'email',
            key: 'email'
          },
          {
            title: 'Number',
            dataIndex: 'number',
            key: 'number'
          },
          {
            title: 'Push Enabled',
            key: 'push',
            render: (_, record) => (
              <Tooltip
                title={
                  'Whether the end-user can receive push notifications on their browser (Web Push) or phone (Mobile Push). This field indicates if their device push tokens have been synced with NotificationAPI.'
                }
                mouseEnterDelay={0.4}
                color="magenta"
              >
                <Space
                  size="middle"
                  style={{
                    cursor: 'help'
                  }}
                >
                  {record.webPushTokens && <ChromeFilled />}
                  {record.pushTokens?.find((t) => t.type === 'APN') && (
                    <AppleFilled />
                  )}
                  {record.pushTokens?.find((t) => t.type === 'FCM') && (
                    <AndroidFilled />
                  )}
                  {!record.webPushTokens &&
                    (!record.pushTokens || record.pushTokens.length === 0) &&
                    'No'}
                </Space>
              </Tooltip>
            )
          },
          {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
              <Space size="middle">
                <Button
                  icon={<ZoomInOutlined />}
                  onClick={() => {
                    history.push(
                      `/${accountId}/logs?user=${encodeURIComponent(
                        record.id
                      )}&start=${new Date().getTime() / 1000 - 86400}&end=${
                        new Date().getTime() / 1000
                      }`
                    );
                  }}
                >
                  Logs
                </Button>
                <Button onClick={() => setPrefsUserId(record.id)}>
                  Preferences
                </Button>
                <Popconfirm
                  title="User Data Deletion"
                  // description="Are you sure you want to delete this user's data?This completely deletes the user, their in-app notifications, and their preferences from NotificationAPI."
                  description={
                    <>
                      <Paragraph>
                        Are you sure you want to delete this user's data?
                      </Paragraph>
                      <Paragraph>
                        This completely deletes the user, their in-app
                        notifications, and their preferences from
                        NotificationAPI.
                      </Paragraph>
                    </>
                  }
                  placement="left"
                  okText="Delete this user"
                  okButtonProps={{ type: 'primary', danger: true }}
                  onConfirm={() => onUserDataDelete(record.id)}
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    loading={loading[record.id]}
                  >
                    Delete
                  </Button>
                </Popconfirm>
              </Space>
            )
          }
        ]}
      />
    </Content>
  );
};

const getUsers = async (clientId: string): Promise<User[]> => {
  await setAWSCreds();
  const ddb = new DynamoDB.DocumentClient();
  const res: User[] = [];
  let nextToken;
  do {
    const userItems: PromiseResult<
      DynamoDB.DocumentClient.QueryOutput,
      AWSError
    > = await ddb
      .query({
        KeyConditionExpression: 'envId = :envId',
        ExpressionAttributeValues: {
          ':envId': clientId
        },
        TableName: 'usersTable1',
        ExclusiveStartKey: nextToken,
        Limit: 1000
      })
      .promise();
    res.push(...(userItems.Items as User[]));
    nextToken = userItems.LastEvaluatedKey;
  } while (nextToken);
  return res;
};

export default UsersContainer;
